import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Sparkles, Check, Loader2 } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { BRAND_COLORS, withAlpha } from "../../lib/bookingStatus";

/**
 * Feb 2026 · Master-Editor für die Inklusiv-Leistungen (Standard-Services).
 * Diese Liste ist die Basis der „Vorbereitung"-Phase im BookingProgressTimeline
 * und erscheint bei jeder Buchung unter „Leistungen dieser Abwesenheit".
 *
 * User-Directive: „Einfache Liste mit Bearbeiten- und Löschen-Button pro
 * Eintrag." Kein Drag-and-Drop, keine Live-Preview — nur reiner CRUD.
 */

const SUBCATEGORY_LABEL = {
  interior: "Innenbereich",
  outdoor: "Außenbereich",
  management: "Verwaltung",
};
const SUBCATEGORIES = ["interior", "outdoor", "management"];

export function StandardServicesSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/standard-services");
      const list = Array.isArray(data) ? [...data] : [];
      list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setItems(list);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const doDelete = async (item) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`„${item.name_de}" endgültig aus dem Master-Katalog entfernen?`)) return;
    setBusy(true);
    try {
      await api.delete(`/team/standard-services/${item.key}`);
      toast.success("Inklusiv-Leistung entfernt.");
      await load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const grouped = SUBCATEGORIES.map((sub) => ({
    sub,
    list: items.filter((it) => (it.subcategory || "management") === sub),
  }));

  return (
    <section
      className="bg-white border border-navy/10 p-6 space-y-4"
      data-testid="master-standard-services-section"
    >
      <header className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: withAlpha(BRAND_COLORS.gold, 0.14), color: BRAND_COLORS.goldDark }}
        >
          <Sparkles className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg text-navy leading-tight">
            Vorbereitung · Inklusiv-Leistungen
          </div>
          <div className="text-[12px] text-navy-dark/60">
            {items.length} Leistung{items.length === 1 ? "" : "en"} · Basis der Vorbereitungs-Phase jeder Buchung
          </div>
        </div>
        <button
          type="button"
          onClick={() => setAdding(true)}
          disabled={busy || adding}
          data-testid="master-std-add-btn"
          className="inline-flex items-center gap-1.5 bg-navy text-ivory hover:bg-navy-dark disabled:opacity-50 px-3 py-2 text-[11.5px] tracking-[0.14em] uppercase"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2} />
          Hinzufügen
        </button>
      </header>

      {loading ? (
        <div className="text-[13px] text-navy-dark/60 italic p-3 inline-flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Wird geladen …
        </div>
      ) : (
        <div className="space-y-5">
          {adding && (
            <StandardServiceAddRow
              onCancel={() => setAdding(false)}
              onSaved={() => { setAdding(false); load(); }}
            />
          )}
          {grouped.map(({ sub, list }) =>
            list.length === 0 ? null : (
              <div key={sub} data-testid={`master-std-group-${sub}`}>
                <div className="label-caps text-navy-dark/70 mb-2">
                  {SUBCATEGORY_LABEL[sub]} · {list.length}
                </div>
                <ul className="space-y-1.5 list-none pl-0 m-0">
                  {list.map((it) => (
                    <StandardServiceRow
                      key={it.key}
                      item={it}
                      isEditing={editingKey === it.key}
                      onEdit={() => setEditingKey(it.key)}
                      onCancel={() => setEditingKey(null)}
                      onDelete={() => doDelete(it)}
                      onSaved={() => { setEditingKey(null); load(); }}
                      busy={busy}
                    />
                  ))}
                </ul>
              </div>
            ),
          )}
          {items.length === 0 && !adding && (
            <div className="text-[13px] text-navy-dark/60 italic p-3 border border-dashed border-navy/15">
              Noch keine Inklusiv-Leistungen — bitte oben „Hinzufügen".
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function StandardServiceRow({ item, isEditing, onEdit, onCancel, onDelete, onSaved, busy }) {
  const [nameDe, setNameDe] = useState(item.name_de || "");
  const [nameEn, setNameEn] = useState(item.name_en || "");
  const [sub, setSub] = useState(item.subcategory || "management");
  const [order, setOrder] = useState(item.order ?? 0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setNameDe(item.name_de || "");
    setNameEn(item.name_en || "");
    setSub(item.subcategory || "management");
    setOrder(item.order ?? 0);
  }, [item, isEditing]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/team/standard-services/${item.key}`, {
        name_de: nameDe.trim(),
        name_en: nameEn.trim(),
        subcategory: sub,
        order: Number(order) || 0,
      });
      toast.success("Gespeichert.");
      onSaved?.();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!isEditing) {
    return (
      <li
        className="flex items-center gap-2 border border-navy/10 bg-white px-3 py-2"
        data-testid={`master-std-row-${item.key}`}
      >
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] text-navy leading-snug">{item.name_de}</div>
          <div className="text-[11.5px] text-navy-dark/50 mt-0.5">
            {item.name_en} · Key <code className="text-navy-dark/70">{item.key}</code> · Reihenfolge {item.order ?? 0}
          </div>
        </div>
        <button
          type="button"
          onClick={onEdit}
          disabled={busy}
          data-testid={`master-std-edit-${item.key}`}
          aria-label="Bearbeiten"
          className="text-navy-dark/50 hover:text-navy p-1.5 border border-navy/15 hover:border-navy/40"
        >
          <Pencil className="w-3.5 h-3.5" strokeWidth={1.7} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={busy}
          data-testid={`master-std-delete-${item.key}`}
          aria-label="Löschen"
          className="text-navy-dark/50 hover:text-red-attn p-1.5 border border-navy/15 hover:border-red-attn"
        >
          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.7} />
        </button>
      </li>
    );
  }
  return (
    <li className="border border-gold bg-ivory/50 p-3" data-testid={`master-std-row-${item.key}-editing`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <label className="text-[11.5px] tracking-[0.14em] uppercase text-navy-dark/60 flex flex-col gap-1.5">
          Name (DE) *
          <input
            type="text" value={nameDe} onChange={(e) => setNameDe(e.target.value)} maxLength={180}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px] text-navy tracking-normal normal-case"
            data-testid={`master-std-input-de-${item.key}`}
          />
        </label>
        <label className="text-[11.5px] tracking-[0.14em] uppercase text-navy-dark/60 flex flex-col gap-1.5">
          Name (EN)
          <input
            type="text" value={nameEn} onChange={(e) => setNameEn(e.target.value)} maxLength={180}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px] text-navy tracking-normal normal-case"
            data-testid={`master-std-input-en-${item.key}`}
          />
        </label>
        <label className="text-[11.5px] tracking-[0.14em] uppercase text-navy-dark/60 flex flex-col gap-1.5">
          Kategorie
          <select
            value={sub} onChange={(e) => setSub(e.target.value)}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px] text-navy tracking-normal normal-case"
            data-testid={`master-std-input-sub-${item.key}`}
          >
            <option value="interior">Innenbereich</option>
            <option value="outdoor">Außenbereich</option>
            <option value="management">Verwaltung</option>
          </select>
        </label>
        <label className="text-[11.5px] tracking-[0.14em] uppercase text-navy-dark/60 flex flex-col gap-1.5">
          Reihenfolge
          <input
            type="number" value={order} onChange={(e) => setOrder(e.target.value)}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px] text-navy tracking-normal"
            data-testid={`master-std-input-order-${item.key}`}
          />
        </label>
      </div>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button" onClick={onCancel} disabled={saving}
          data-testid={`master-std-cancel-${item.key}`}
          className="text-[11px] tracking-[0.14em] uppercase text-navy-dark/70 hover:text-navy px-3 py-1.5 border border-navy/15 disabled:opacity-50"
        >
          Abbrechen
        </button>
        <button
          type="button" onClick={save} disabled={saving || !nameDe.trim()}
          data-testid={`master-std-save-${item.key}`}
          className="inline-flex items-center gap-1.5 bg-navy text-ivory hover:bg-navy-dark disabled:opacity-60 px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase"
        >
          <Check className="w-3.5 h-3.5" strokeWidth={2} />
          {saving ? "…" : "Speichern"}
        </button>
      </div>
    </li>
  );
}

function StandardServiceAddRow({ onCancel, onSaved }) {
  const [key, setKey] = useState("");
  const [nameDe, setNameDe] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [sub, setSub] = useState("management");
  const [saving, setSaving] = useState(false);

  const derivedKey = (key || nameDe)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 32);

  const save = async () => {
    if (!nameDe.trim()) { toast.error("Name (DE) fehlt."); return; }
    if (!derivedKey) { toast.error("Key konnte nicht abgeleitet werden — bitte manuell setzen."); return; }
    setSaving(true);
    try {
      await api.post("/team/standard-services", {
        key: derivedKey,
        name_de: nameDe.trim(),
        name_en: (nameEn || nameDe).trim(),
        subcategory: sub,
        order: 999,
      });
      toast.success("Inklusiv-Leistung angelegt.");
      onSaved?.();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="border-2 border-dashed border-gold p-4 bg-ivory/40" data-testid="master-std-add-row">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <label className="text-[11.5px] tracking-[0.14em] uppercase text-navy-dark/60 flex flex-col gap-1.5">
          Name (DE) *
          <input
            type="text" value={nameDe} onChange={(e) => setNameDe(e.target.value)}
            placeholder="z. B. Postbox leeren"
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px] text-navy tracking-normal normal-case"
            data-testid="master-std-add-de"
          />
        </label>
        <label className="text-[11.5px] tracking-[0.14em] uppercase text-navy-dark/60 flex flex-col gap-1.5">
          Name (EN)
          <input
            type="text" value={nameEn} onChange={(e) => setNameEn(e.target.value)}
            placeholder="Fällt leer → wird aus DE übernommen"
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px] text-navy tracking-normal normal-case"
            data-testid="master-std-add-en"
          />
        </label>
        <label className="text-[11.5px] tracking-[0.14em] uppercase text-navy-dark/60 flex flex-col gap-1.5">
          Key (optional · leer = automatisch)
          <input
            type="text" value={key} onChange={(e) => setKey(e.target.value)}
            placeholder={derivedKey || "z. B. mailbox"}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px] text-navy tracking-normal normal-case font-mono"
            data-testid="master-std-add-key"
          />
        </label>
        <label className="text-[11.5px] tracking-[0.14em] uppercase text-navy-dark/60 flex flex-col gap-1.5">
          Kategorie
          <select
            value={sub} onChange={(e) => setSub(e.target.value)}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-2 py-1.5 text-[13px] text-navy tracking-normal normal-case"
            data-testid="master-std-add-sub"
          >
            <option value="interior">Innenbereich</option>
            <option value="outdoor">Außenbereich</option>
            <option value="management">Verwaltung</option>
          </select>
        </label>
      </div>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button" onClick={onCancel} disabled={saving}
          data-testid="master-std-add-cancel"
          className="text-[11px] tracking-[0.14em] uppercase text-navy-dark/70 hover:text-navy px-3 py-1.5 border border-navy/15 disabled:opacity-50"
        >
          Abbrechen
        </button>
        <button
          type="button" onClick={save} disabled={saving || !nameDe.trim()}
          data-testid="master-std-add-save"
          className="inline-flex items-center gap-1.5 bg-navy text-ivory hover:bg-navy-dark disabled:opacity-60 px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2} />
          {saving ? "…" : "Anlegen"}
        </button>
      </div>
    </div>
  );
}
